import { collectionGroup, DocumentData, getDocs, query, QueryDocumentSnapshot, where } from "firebase/firestore";
import { DateTime, Interval } from "luxon";
import { GetManyReferenceParams, GetManyReferenceResult } from "react-admin";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { firestore } from "../firebase";
import { RaRecord } from "react-admin";
import { isDateRange, isValidInterval } from "../model/DateRange";

export interface PrivateAppointment extends RaRecord {
  id: string;
  start: DateTime;
  end: DateTime;
}

class InstructorPrivateAppointmentsProvider {
  async getManyReference(
    _: string,
    { id, target, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<PrivateAppointment>> {
    if (target !== "instructorId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "instructorId"`);
    }
    const { dateRange, ...otherFilters } = filter;
    let isInDateRange: (privateAppointment: PrivateAppointment) => boolean;
    if (!dateRange) {
      throw new Error("filter.dateRange is required");
    } else if (isDateRange(dateRange)) {
      const interval = Interval.fromDateTimes(dateRange[0].at(0), dateRange[1].nextDay.at(0));
      isInDateRange = (privateAppointment) => interval.contains(privateAppointment.start);
    } else if (isValidInterval(dateRange)) {
      isInDateRange = (privateAppointment) => dateRange.contains(privateAppointment.start);
    } else {
      throw new Error(
        `Invalid filter.dateRange: ${JSON.stringify(dateRange)} -- must be a valid DateRange or a valid Interval`,
      );
    }
    const query_ = query(collectionGroup(firestore, "private_appointments"), where("userUid", "==", id));
    const snapshot = await getDocs(query_.withConverter(privateAppointmentsConverter));
    const privateAppointments = snapshot.docs
      .map((it) => it.data())
      .flat()
      .filter(isInDateRange);
    return applyPagination(applySort(applyFilter(privateAppointments, { ...otherFilters }), sort), pagination);
  }
}

export const instructorPrivateAppointmentsProvider = new InstructorPrivateAppointmentsProvider();

const privateAppointmentsConverter = {
  toFirestore: (_: PrivateAppointment[]): DocumentData => {
    throw new Error("Not implemented");
  },

  fromFirestore: (doc: QueryDocumentSnapshot): PrivateAppointment[] => {
    const docData = doc.data();
    return docData.data.map(
      (it: { [field: string]: any }): PrivateAppointment => ({
        id: it.uid,
        start: DateTime.fromJSDate(it.start.toDate()),
        end: DateTime.fromJSDate(it.end.toDate()),
      }),
    );
  },
};
